import { ProgressSpinner } from "primereact/progressspinner";
import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Select from "react-select";
import Http from "../../../Common/Http";

// Quill editor configuration for comments
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = ["header", "bold", "italic", "underline", "color", "background", "list", "bullet", "link"];

const StatusUpdate = ({ taskId, currentStatus, validStatusTransitions, setVisible, fetchData, onSuccess }) => {
  const [status, setStatus] = useState(currentStatus || "To Do");
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Default status options (all statuses)
  const allStatusOptions = [
    { label: "To Do", value: "To Do" },
    { label: "In Progress", value: "In Progress" },
    { label: "Review", value: "Review" },
    { label: "Done", value: "Done" },
  ];

  // Filter status options based on valid transitions if provided
  const statusOptions = validStatusTransitions
    ? allStatusOptions.filter((option) => validStatusTransitions.includes(option.value))
    : allStatusOptions;

  // Handle status change
  const handleStatusChange = (selectedOption) => {
    setStatus(selectedOption ? selectedOption.value : "To Do");
  };

  // Handle comment change
  const handleCommentChange = (content) => {
    setComment(content);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const formData = {
        status: status,
        comment: comment,
      };

      console.log("Updating task status:", formData);

      // Validate that the selected status is allowed
      if (validStatusTransitions && !validStatusTransitions.includes(status)) {
        setError("This status transition is not allowed. Tasks can only move forward in the workflow.");
        setLoading(false);
        return;
      }

      const res = await Http.patch(`/task/${taskId}/status`, formData);

      if (res.data) {
        if (onSuccess) {
          try {
            // Fetch the updated task data to return it to the parent component
            const taskRes = await Http.get(`/task/${taskId}`);
            if (taskRes.data && taskRes.data.data) {
              // Return the complete updated task data
              onSuccess(taskRes.data.data);
            } else {
              // If we can't get the full task data, at least return the updated status
              onSuccess({ id: taskId, status: status });
            }
          } catch (fetchError) {
            console.error("Error fetching updated task data:", fetchError);
            // If fetching fails, still provide the basic updated data
            onSuccess({ id: taskId, status: status });
          }
        } else if (fetchData) {
          // For backward compatibility
          setVisible(false);
          fetchData();
        } else {
          setVisible(false);
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      setError(error.response?.data?.message || "Failed to update task status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading ? (
        <div className="text-center p-5">
          <ProgressSpinner />
          <p className="mt-2">Updating task status...</p>
        </div>
      ) : (
        <div className="container-fluid">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {validStatusTransitions && validStatusTransitions.length <= 1 && (
            <div className="alert alert-info" role="alert">
              <i className="pi pi-info-circle mr-2"></i>
              This task cannot be moved backward in the workflow. You can only move tasks forward.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row mb-4">
              <div className="col-12">
                <label htmlFor="status" className="form-label">
                  Status <span className="text-danger">*</span>
                </label>
                <Select
                  id="status"
                  name="status"
                  value={statusOptions.find((option) => option.value === status) || statusOptions[0]}
                  options={statusOptions}
                  onChange={handleStatusChange}
                  placeholder="Select status"
                  className="basic-select"
                  classNamePrefix="select"
                  isDisabled={statusOptions.length <= 1}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12">
                <label htmlFor="comment" className="form-label">
                  Comment (Optional)
                </label>
                <ReactQuill
                  theme="snow"
                  value={comment}
                  onChange={handleCommentChange}
                  placeholder="Add a comment about this status change"
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: "150px", marginBottom: "40px" }}
                />
                <small className="text-muted">A comment helps the team understand why the status was changed</small>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <button type="submit" className="btn btn-primary float-end" disabled={loading || statusOptions.length <= 1}>
                  {loading ? (
                    <span>
                      <i className="fa fa-spinner fa-spin"></i> Updating...
                    </span>
                  ) : (
                    "Update Status"
                  )}
                </button>
                <button type="button" className="btn btn-secondary float-end me-2" onClick={() => setVisible(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default StatusUpdate;
